import {
    ADDRESS_LABEL_ALIASES,
    DBA_LABEL,
} from '@/modules/entity-management/utils/normalize-export-csv/exportCsvConstants';

export interface NormalizeExportCsvContext {
    // Canonical bulk-import labels, in canonical order (component's entityColumnsLabel getter).
    entityColumnsLabel: string[];
    // Export header (custom-property key / dotted key) -> import label.
    keyToLabel: Record<string, string>;
    // Canonical labels of boolean custom-property columns. The export emits "true"/"false"
    // while the import expects "Yes"/"No", so these columns get a boolean value transform.
    // Keyed per custom-property (not a fixed label), so they cannot live in VALUE_TRANSFORMS.
    booleanLabels?: string[];
    // Canonical labels of collateral custom-property columns. The export emits a single
    // JSON-array string (e.g. '[{...},{...}]') while the import expects pipe-separated JSON
    // objects (e.g. '{...}|{...}'). Keyed per custom-property, not a fixed label.
    collateralLabels?: string[];
    // Canonical labels of Equipment custom-property columns. The export uses the same
    // single-JSON-array encoding as collaterals, and gets the same conversion. Keyed per
    // custom-property, not a fixed label.
    equipmentLabels?: string[];
    // Canonical labels of list (array-of-primitive) custom-property columns, e.g. LIST_EMAIL
    // and LIST_STRING. The export emits a single JSON-array string (e.g. '["a","b"]') while
    // the import expects pipe-separated primitives (a|b). Keyed per custom-property, not a
    // fixed label. (Regression: PRODUCT-25994.)
    listLabels?: string[];
}

export interface NormalizeExportCsvResult {
    rows: string[][];
    droppedColumns: string[];
}

// Converts the backend JSON-array encoding of primitives (e.g. ["a","b"]) into the
// pipe-separated form the import expects (a|b). Leaves anything that is not a JSON array
// untouched. Shared by the DBA standard column and list (array-of-primitive) custom
// properties (LIST_EMAIL / LIST_STRING).
function normalizePrimitiveArrayValue(value: string): string {
    const trimmed = value.trim();
    if (!trimmed.startsWith('[')) {
        return value;
    }
    try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
            return parsed.join('|');
        }
    } catch {
        // not valid JSON — fall through and return the original value
    }
    return value;
}

// Converts the export's single JSON-array encoding (e.g. [{...},{...}]) into the
// pipe-separated JSON-object form the bulk import expects ({...}|{...}). Leaves any
// value that is not a JSON array untouched (already pipe form, empty, etc.). Shared by
// collateral and Equipment custom properties. The Producers column keeps its JSON array, which the
// import reads whole.
function normalizeObjectArrayValue(value: string): string {
    const trimmed = value.trim();
    if (!trimmed.startsWith('[')) {
        return value;
    }
    try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
            return parsed.map((item) => JSON.stringify(item)).join('|');
        }
    } catch {
        // not valid JSON — return the original value unchanged
    }
    return value;
}

/** The import label an export header stands for: an address alias, an import label, or a custom-property
 * key. Null for a read-only column. */
export function resolveExportHeader(
    label: string, ctx: Pick<NormalizeExportCsvContext, 'entityColumnsLabel' | 'keyToLabel'>,
): string | null {
    const trimmed = label.trim();
    if (trimmed in ADDRESS_LABEL_ALIASES) {
        return ADDRESS_LABEL_ALIASES[trimmed];
    }
    if (ctx.entityColumnsLabel.includes(trimmed)) {
        return trimmed;
    }
    return ctx.keyToLabel[trimmed] ?? null;
}

export const VALUE_TRANSFORMS: Record<string, (value: string) => string> = {
    [DBA_LABEL]: normalizePrimitiveArrayValue,
};

// Converts the backend boolean encoding ("true"/"false") into the "Yes"/"No" form the
// import expects. Comparison is trimmed and case-insensitive. Anything else (already
// "Yes"/"No", an empty cell, unexpected text) is passed through unchanged.
function normalizeBooleanValue(value: string): string {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') {
        return 'Yes';
    }
    if (normalized === 'false') {
        return 'No';
    }
    return value;
}

/**
 * Rewrites a "Download Data" export CSV into the canonical bulk-import shape:
 *  - aliases the bare address headers to "Entity Address *",
 *  - maps custom-property key headers to import labels via ctx.keyToLabel,
 *  - keeps columns whose resolved label is a known import label, drops the rest,
 *  - synthesizes any import columns absent from the export as empty,
 *  - applies per-column value transforms (DBA by fixed label, booleans by booleanLabels).
 *
 * The output header row equals ctx.entityColumnsLabel exactly, so the existing
 * bulk-import validation passes unchanged.
 */
export function normalizeExportCsv(
    rows: string[][], ctx: NormalizeExportCsvContext,
): NormalizeExportCsvResult {
    const {
        entityColumnsLabel, keyToLabel, booleanLabels, collateralLabels, equipmentLabels, listLabels,
    } = ctx;
    const booleanLabelSet = new Set(booleanLabels ?? []);
    const collateralLabelSet = new Set(collateralLabels ?? []);
    const equipmentLabelSet = new Set(equipmentLabels ?? []);
    const listLabelSet = new Set(listLabels ?? []);
    const header = (rows[0] || []).map((label) => label.trim());

    const resolved = header.map((label) => resolveExportHeader(label, { entityColumnsLabel, keyToLabel }));
    const droppedColumns = header.filter((_, index) => resolved[index] === null);

    const labelToSourceIndex: Record<string, number> = {};
    resolved.forEach((label, index) => {
        if (label !== null && !(label in labelToSourceIndex)) {
            labelToSourceIndex[label] = index;
        }
    });

    const dataRows = rows.slice(1).map((row) =>
        entityColumnsLabel.map((label) => {
            const sourceIndex = labelToSourceIndex[label];
            const value = sourceIndex === undefined ? '' : (row[sourceIndex] ?? '');
            // Fixed-label transforms (DBA), boolean-label, collateral-label, equipment-label,
            // and list-label transforms are mutually exclusive — at most one applies per column.
            const transform = VALUE_TRANSFORMS[label]
                ?? (booleanLabelSet.has(label) ? normalizeBooleanValue : undefined)
                ?? (collateralLabelSet.has(label) ? normalizeObjectArrayValue : undefined)
                ?? (equipmentLabelSet.has(label) ? normalizeObjectArrayValue : undefined)
                ?? (listLabelSet.has(label) ? normalizePrimitiveArrayValue : undefined);
            return transform ? transform(value) : value;
        }));

    return {
        rows: [ [ ...entityColumnsLabel ], ...dataRows ],
        droppedColumns,
    };
}
